/**
 * Container for the contract file upload zone.
 *
 * Wraps the platform-specific view (`.web.tsx` / `.native.tsx`) with
 * shared file-validation logic. The container is intentionally a thin
 * passthrough — it owns no state of its own. State for staged files
 * lives in the parent {@link ContractUploadSectionContainer} so the
 * upload section can render the staged-files list independently of the
 * drop zone.
 *
 * Expo's file-extension resolver picks the right view at build time,
 * so importing `./FileUploadZoneView` resolves to `FileUploadZoneView.web.tsx`
 * on web and `FileUploadZoneView.native.tsx` on iOS/Android.
 * @module features/contracts/components/FileUploadZone/FileUploadZoneContainer
 */

import React, { memo, useCallback } from "react";

import { CONTRACT_ACCEPTED_MIME_TYPES } from "@/features/contracts/constants";
import type {
  FileUploadZoneProps,
  StagedContractFile,
} from "@/features/contracts/types";
import { validateContractFile } from "@/features/contracts/utils/validateContractFile";

import FileUploadZoneView from "./FileUploadZoneView";

/**
 * Container for the file upload zone.
 * @param props - Shared upload zone props
 * @param props.disabled - Whether the zone should reject input (e.g. mid-upload)
 * @param props.multiple - Whether multiple files can be selected at once (default true)
 * @param props.documentType - Active document type ("CONTRACT" | "TRANSFER_AGREEMENT"),
 *   forwarded to the platform view so its drop-zone heading matches the upload section.
 * @param props.onFilesSelected - Invoked with the accepted files after validation
 * @param props.onInvalidFiles - Optional callback receiving rejected files + reasons
 * @returns The platform-specific upload zone view
 */
function FileUploadZoneContainer({
  disabled = false,
  multiple = true,
  documentType = "CONTRACT",
  onFilesSelected,
  onInvalidFiles,
}: FileUploadZoneProps) {
  const handleFiles = useCallback(
    (files: readonly StagedContractFile[]) => {
      const validations = files.map(file => ({
        file,
        result: validateContractFile(file),
      }));

      const accepted = validations
        .filter(({ result }) => result.valid)
        .map(({ file }) => file);

      const rejected = validations
        .filter(({ result }) => !result.valid)
        .map(({ file, result }) => ({
          name: file.name,
          reason: result.reason ?? "File type not supported.",
        }));

      if (accepted.length > 0) {
        onFilesSelected(accepted);
      }
      if (rejected.length > 0 && onInvalidFiles) {
        onInvalidFiles(rejected);
      }
    },
    [onFilesSelected, onInvalidFiles]
  );

  return (
    <FileUploadZoneView
      disabled={disabled}
      multiple={multiple}
      documentType={documentType}
      acceptedMimeTypes={CONTRACT_ACCEPTED_MIME_TYPES}
      onFilesPicked={handleFiles}
    />
  );
}

FileUploadZoneContainer.displayName = "FileUploadZoneContainer";

export default memo(FileUploadZoneContainer);
